import {
  Serializer,
  SurveyModel,
  QuestionPanelDynamicModel,
  PanelModel,
  Question,
} from "survey-core";

// Property name for paneldynamic questions: array of question names to copy from first panel when new panels are added.
const COPY_FROM_FIRST_PROPERTY = "copyValueFromFirstPanel";
let propertyRegistered = false;

export const registerPanelDynamicCustomizations = () => {
  if (propertyRegistered) {
    return;
  }
  Serializer.addProperty("paneldynamic", {
    name: `${COPY_FROM_FIRST_PROPERTY}:itemvalue[]`,
    category: "general",
    visible: true,
    // using itemvalue[] to allow either ["q1","q2"] or [{value:"q1"}, ...] per SurveyJS format; we will normalize to string names
    default: [],
    displayName: "Copy values from first panel (question names)",
  });
  propertyRegistered = true;
};

export const applyPanelDynamicCustomizations = (model: SurveyModel) => {
  registerPanelDynamicCustomizations();

  // Accessor to avoid using any
  const getListOfQuestionsToCopy = (q: QuestionPanelDynamicModel): string[] => {
    const questionValue = (q as unknown as Record<string, unknown>)[
      COPY_FROM_FIRST_PROPERTY
    ] as unknown;
    if (!questionValue) {
      return [];
    }
    if (Array.isArray(questionValue)) {
      return questionValue
        .map((r) => {
          if (!r) {
            return undefined;
          }
          // Accept plain string values or objects with value/text
          if (typeof r === "string") {
            return r;
          }
          if (
            typeof r === "object" &&
            r !== null &&
            "value" in (r as Record<string, unknown>) &&
            typeof (r as { value?: unknown }).value === "string"
          ) {
            return (r as { value?: string }).value as string;
          }
          return undefined;
        })
        .filter((v): v is string => !!v);
    }
    return [];
  };

  const shouldProcess = (
    q: Question | QuestionPanelDynamicModel,
  ): q is QuestionPanelDynamicModel => {
    if ((q as Question).getType?.() !== "paneldynamic") {
      return false;
    }
    const list = getListOfQuestionsToCopy(q as QuestionPanelDynamicModel);
    return Array.isArray(list) && list.length > 0;
  };

  const copyValuesToNewPanel = (
    q: QuestionPanelDynamicModel,
    newPanel: PanelModel,
    panelIndex: number,
  ) => {
    if (panelIndex === 0) {
      // nothing to copy into first panel
      return;
    }
    const firstPanel = q.panels?.[0];
    if (!firstPanel) {
      return;
    }
    const copyList = getListOfQuestionsToCopy(q);
    if (copyList.length === 0) {
      return;
    }

    copyList.forEach((questionName) => {
      const sourceQuestion = firstPanel.getQuestionByName(questionName);
      const targetQuestion = newPanel.getQuestionByName(questionName);
      if (!sourceQuestion || !targetQuestion) return;
      const hasValue =
        targetQuestion.value !== undefined &&
        targetQuestion.value !== null &&
        targetQuestion.value !== "";
      if (hasValue) return;
      const sourceVal = sourceQuestion.value;
      if (sourceVal === undefined) return;
      // Set via API
      try {
        targetQuestion.value = sourceVal;
      } catch (_) {
        /* noop */
      }
      // Ensure underlying paneldynamic value array is updated so SurveyJS renders selected display text.
      try {
        const rawValue = q.value;
        const arr: Record<string, unknown>[] = Array.isArray(rawValue)
          ? [...rawValue]
          : [];
        // Guarantee length
        while (arr.length <= panelIndex) arr.push({});
        if (!arr[panelIndex]) arr[panelIndex] = {};
        arr[panelIndex][questionName] = sourceVal;
        q.value = arr;
      } catch (_) {
        /* noop */
      }
    });
  };

  // Initial pass: existing paneldynamic questions may already have >1 panels due to pre-filled data.
  model.getAllQuestions().forEach((question) => {
    if (!shouldProcess(question)) {
      return;
    }
    const qpd = question as QuestionPanelDynamicModel;
    // For panels beyond the first that are empty, copy values.
    qpd.panels?.forEach((panel, idx) => copyValuesToNewPanel(qpd, panel, idx));
  });

  model.onDynamicPanelAdded.add((sender, options) => {
    const q = options.question as QuestionPanelDynamicModel;
    if (!shouldProcess(q)) return;
    // Defer to next microtask to allow SurveyJS internal panel/question rendering to finalize.
    setTimeout(() => {
      copyValuesToNewPanel(q, options.panel, options.panelIndex);
    }, 0);
  });
};
